import { Helmet } from '@dr.pogodin/react-helmet';
import type React from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MultiLang } from '../config';
import Functions from '../functions';
import type { BrainCoordinate } from '../nimgcenter/lib/BrainCoordinateUtil';
import ItemType from '../xoonips/item-type';
import AdvancedSearchQuery from '../xoonips/lib/AdvancedSearchQuery';
import ItemUtil, {
    type Item,
    type ItemNimgcenter,
    type ItemPaper,
    type SearchResult,
    type SortCondition,
    type SortConditionLimit,
    type SortConditionOrderBy,
    SortConditionOrderDir,
} from '../xoonips/lib/ItemUtil';
import NimgsearchUtil, {
    type BrainCoordinateAxis,
    type BrainCoordinateString,
    type BrainCoordinateType,
    type BrainImageSize,
    type SearchPaperType,
} from './lib/NimgsearchUtil';
import styles from './Nimgsearch.module.css';

interface Props {
    lang: MultiLang;
}

const Nimgsearch: React.FC<Props> = ({ lang }) => {
    const defaultCoord = useMemo(() => ({ x: 0, y: 0, z: 0 }) as BrainCoordinate, []);
    const defaultSortCondition = useMemo(
        () =>
            ({
                limit: 20,
                orderBy: 'title',
                orderDir: SortConditionOrderDir.DESC,
                page: 1,
            }) as SortCondition,
        []
    );

    // State
    const [pointer, setPointer] = useState<BrainCoordinate>({ ...defaultCoord });
    const [coord, setCoord] = useState<BrainCoordinate>({ ...defaultCoord });
    const [coordType, setCoordType] = useState<BrainCoordinateType>('mni');
    const [showSettings, setShowSettings] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [imageSize, setImageSize] = useState<BrainImageSize>('large');
    const [showLines, setShowLines] = useState(true);
    const [showSpots, setShowSpots] = useState(true);
    const [formCoord, setFormCoord] = useState<BrainCoordinateString>({ x: '0', y: '0', z: '0' });
    const [formRadiusRadio, setFormRadiusRadio] = useState('2');
    const [formRadiusSelect, setFormRadiusSelect] = useState('10');
    const [formCoordType, setFormCoordType] = useState<BrainCoordinateType>('mni');
    const [formPaperType, setFormPaperType] = useState<SearchPaperType>('all');
    const [brainAreaLevel, setBrainAreaLevel] = useState(1);
    const [brainAreaIds, setBrainAreaIds] = useState<number[]>([]);
    const [itemType, setItemType] = useState('');
    const [andOr, setAndOr] = useState('and');
    const [query] = useState(new AdvancedSearchQuery());
    const [sortCondition, setSortCondition] = useState<SortCondition>({ ...defaultSortCondition });
    const [searchResult, setSearchResult] = useState<SearchResult>({ total: 0, data: [] });
    const [showInfoItemIds, setShowInfoItemIds] = useState<number[]>([]);
    const [myItems, setMyItems] = useState<ItemNimgcenter[]>([]);
    const [tab, setTab] = useState(0);

    // Refs
    const isActive = useRef(false);
    const brainImageX = useRef<HTMLCanvasElement>(null);
    const brainImageY = useRef<HTMLCanvasElement>(null);
    const brainImageZ = useRef<HTMLCanvasElement>(null);
    const brainAreaSource = useRef<HTMLSelectElement>(null);
    const brainAreaTarget = useRef<HTMLSelectElement>(null);
    const [baseItems, setBaseItems] = useState(new Map<number, Item>());

    // Draw brain image on canvas refs (called from useEffect, not during render)
    const drawBrainImage = useCallback(() => {
        if (!isActive.current) return;

        if (brainImageX.current) {
            NimgsearchUtil.updateBrainImage(
                brainImageX.current,
                coord,
                formCoordType,
                'x',
                imageSize,
                brainAreaIds,
                showLines,
                showSpots ? myItems : []
            );
        }
        if (brainImageY.current) {
            NimgsearchUtil.updateBrainImage(
                brainImageY.current,
                coord,
                formCoordType,
                'y',
                imageSize,
                brainAreaIds,
                showLines,
                showSpots ? myItems : []
            );
        }
        if (brainImageZ.current) {
            NimgsearchUtil.updateBrainImage(
                brainImageZ.current,
                coord,
                formCoordType,
                'z',
                imageSize,
                brainAreaIds,
                showLines,
                showSpots ? myItems : []
            );
        }
    }, [coord, imageSize, brainAreaIds, showLines, showSpots, myItems, formCoordType]);

    // Redraw canvas whenever relevant state changes
    useEffect(() => {
        drawBrainImage();
    }, [drawBrainImage]);

    // Helper to update brain image state (no ref access)
    const updateBrainImage = useCallback(
        (updates: {
            coord?: BrainCoordinate;
            imageSize?: BrainImageSize;
            brainAreaIds?: number[];
            showLines?: boolean;
            showSpots?: boolean;
            myItems?: ItemNimgcenter[];
            formCoordType?: BrainCoordinateType;
        }) => {
            const c = updates.coord ?? coord;
            setFormCoord({ x: c.x.toString(), y: c.y.toString(), z: c.z.toString() });

            if (updates.coord) setCoord(updates.coord);
            if (updates.imageSize) setImageSize(updates.imageSize);
            if (updates.brainAreaIds) setBrainAreaIds(updates.brainAreaIds);
            if (updates.showLines !== undefined) setShowLines(updates.showLines);
            if (updates.showSpots !== undefined) setShowSpots(updates.showSpots);
            if (updates.myItems !== undefined) setMyItems(updates.myItems);
            if (updates.formCoordType) setFormCoordType(updates.formCoordType);
        },
        [coord]
    );

    // Initial mount - only run once on mount
    const updateBrainImageRef = useRef(updateBrainImage);
    useEffect(() => {
        updateBrainImageRef.current = updateBrainImage;
    }, [updateBrainImage]);

    useEffect(() => {
        isActive.current = true;
        updateBrainImageRef.current({ coord: defaultCoord });
        return () => {
            isActive.current = false;
        };
    }, [defaultCoord]);

    const onClickBrainImage = useCallback(
        (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>, axis: BrainCoordinateAxis) => {
            const offset = { x: event.nativeEvent.offsetX, y: event.nativeEvent.offsetY };
            const newCoord = NimgsearchUtil.getBrainCoordinateFromOffset(coord, formCoordType, offset, imageSize, axis);
            setPointer(newCoord);
            updateBrainImage({ coord: newCoord });
        },
        [coord, formCoordType, imageSize, updateBrainImage]
    );

    const onMouseMoveBrainImage = useCallback(
        (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>, axis: BrainCoordinateAxis) => {
            const offset = { x: event.nativeEvent.offsetX, y: event.nativeEvent.offsetY };
            const newPointer = NimgsearchUtil.getBrainCoordinateFromOffset(
                pointer,
                formCoordType,
                offset,
                imageSize,
                axis
            );
            if (newPointer.x !== pointer.x || newPointer.y !== pointer.y || newPointer.z !== pointer.z) {
                setPointer(newPointer);
            }
        },
        [pointer, formCoordType, imageSize]
    );

    const onChnageCoordinate = useCallback((event: React.ChangeEvent<HTMLInputElement>, axis: BrainCoordinateAxis) => {
        const value = event.target.value;
        setFormCoord((prev) => ({ ...prev, [axis]: value }));
    }, []);

    const onClickUpdateButton = useCallback(() => {
        const newCoord = NimgsearchUtil.fixBrainCoordinate(
            {
                x: parseInt(formCoord.x, 10),
                y: parseInt(formCoord.y, 10),
                z: parseInt(formCoord.z, 10),
            },
            formCoordType
        );
        updateBrainImage({ coord: newCoord });
    }, [formCoord, formCoordType, updateBrainImage]);

    const updateSearchResults = useCallback(
        (sc: SortCondition, ct: BrainCoordinateType) => {
            const radius = parseInt(formRadiusRadio !== '' ? formRadiusRadio : formRadiusSelect, 10);
            NimgsearchUtil.searchXoonipsItems(
                coord,
                radius,
                ct,
                formPaperType,
                sc,
                query,
                itemType !== '' ? andOr : '',
                async (result) => {
                    await Promise.all(
                        result.data
                            .filter((item) => {
                                const nc =
                                    item.item_type_name === 'xnpnimgcenter' ? (item as ItemNimgcenter) : undefined;
                                return nc && !baseItems.has(nc.baseitem_id);
                            })
                            .map(async (item) => {
                                const nc = item as ItemNimgcenter;
                                return new Promise<void>((resolve) => {
                                    ItemUtil.get(nc.baseitem_id, (baseItem) => {
                                        if (baseItem !== null) {
                                            setBaseItems((prev) => {
                                                const next = new Map(prev);
                                                next.set(baseItem.item_id, baseItem);
                                                return next;
                                            });
                                        }
                                        resolve();
                                    });
                                });
                            })
                    );
                    setTab(2);
                    setSortCondition(sc);
                    setSearchResult(result);
                    setShowInfoItemIds([]);
                    setCoordType(ct);
                }
            );
        },
        [coord, formRadiusRadio, formRadiusSelect, formPaperType, query, itemType, andOr, baseItems]
    );

    const renderListItem = useCallback(
        (item: Item) => {
            const nimgcenterItem = item.item_type_name === 'xnpnimgcenter' ? (item as ItemNimgcenter) : undefined;
            if (typeof nimgcenterItem === 'undefined') {
                return <div>Unexpected Error: {item.title}</div>;
            }
            const baseItem = baseItems.get(nimgcenterItem.baseitem_id);
            const paperItem = baseItem?.item_type_name === 'xnppaper' ? (baseItem as ItemPaper) : undefined;
            const isShowInfo = showInfoItemIds.includes(item.item_id);
            const isShowSpot = !!myItems.find((value) => value.item_id === item.item_id);
            return (
                <div className={styles.listItem}>
                    <div>
                        <a href={ItemUtil.getUrl(item)} target="_blank" rel="noopener noreferrer">
                            {Functions.mlang(item.title, lang)}
                        </a>
                    </div>
                    {paperItem && (
                        <>
                            <div>
                                {paperItem.author.map((author, index) => (
                                    // biome-ignore lint/suspicious/noArrayIndexKey: author names may repeat
                                    <Fragment key={index}>
                                        <span>{author}</span>
                                        {index + 1 !== paperItem.author.length && <>, </>}
                                    </Fragment>
                                ))}
                            </div>
                            <div>
                                <span>({paperItem.publication_year})</span> <em>{paperItem.journal}</em>,{' '}
                                <span>{paperItem.volume}</span>, <span>{paperItem.page}</span>
                                <input
                                    style={{ fontWeight: isShowInfo ? 'bold' : 'normal' }}
                                    type="button"
                                    value="[INFO]"
                                    onClick={() => {
                                        setShowInfoItemIds((prev) =>
                                            isShowInfo
                                                ? prev.filter((id) => id !== item.item_id)
                                                : prev.concat(item.item_id)
                                        );
                                    }}
                                />
                                <input
                                    style={{ fontWeight: isShowSpot ? 'bold' : 'normal' }}
                                    type="button"
                                    value="[SPOT]"
                                    onClick={() => {
                                        const newMyItems = isShowSpot
                                            ? myItems.filter((value) => value.item_id !== item.item_id)
                                            : myItems.concat(nimgcenterItem);
                                        updateBrainImage({ myItems: newMyItems });
                                    }}
                                />
                            </div>
                        </>
                    )}
                    {isShowInfo && (
                        <div className={styles.listItemCoordinates}>
                            {nimgcenterItem.coord_type_name}:{' '}
                            {nimgcenterItem.coordinate.map((c, index) => (
                                // biome-ignore lint/suspicious/noArrayIndexKey: coordinates may repeat
                                <Fragment key={index}>
                                    {index !== 0 && <>, </>}
                                    <span>
                                        ({c.x}, {c.y}, {c.z})
                                    </span>
                                </Fragment>
                            ))}
                        </div>
                    )}
                </div>
            );
        },
        [showInfoItemIds, myItems, lang, updateBrainImage, baseItems]
    );

    const renderBrainAreaTab = useCallback(() => {
        return (
            <div className={styles.tabBrainArea}>
                <div className={styles.tabBrainAreaSource}>
                    <div>
                        <select
                            value={brainAreaLevel}
                            onChange={(e) => setBrainAreaLevel(parseInt(e.target.value, 10))}
                        >
                            {[1, 2, 3, 4, 5].map((value) => (
                                <option key={value} value={value}>
                                    Level {value}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <select ref={brainAreaSource} className={styles.tabBrainAreaSelect} size={12} multiple>
                            {NimgsearchUtil.getBrainAreaNames(brainAreaLevel).map((value, index) => {
                                const id = NimgsearchUtil.getBrainAreaId(brainAreaLevel, index);
                                return (
                                    <option key={id} value={id}>
                                        {value}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                </div>
                <div className={styles.tabBrainAreaButtons}>
                    <p>
                        <input
                            type="button"
                            value="->"
                            onClick={() => {
                                const source = brainAreaSource.current;
                                if (source !== null) {
                                    const newBrainAreaIds = brainAreaIds.concat();
                                    for (let ii = 0; ii < source.selectedOptions.length; ii++) {
                                        const value = parseInt(source.selectedOptions[ii].value, 10);
                                        if (!brainAreaIds.includes(value)) {
                                            newBrainAreaIds.push(value);
                                        }
                                    }
                                    if (
                                        brainAreaIds.length !== newBrainAreaIds.length ||
                                        !brainAreaIds.every((value, index) => value === newBrainAreaIds[index])
                                    ) {
                                        updateBrainImage({ brainAreaIds: newBrainAreaIds });
                                    }
                                }
                            }}
                        />
                    </p>
                    <p>
                        <input
                            type="button"
                            value="<-"
                            onClick={() => {
                                const target = brainAreaTarget.current;
                                if (target !== null) {
                                    const ids: number[] = [];
                                    for (let ii = 0; ii < target.selectedOptions.length; ii++) {
                                        ids.push(parseInt(target.selectedOptions[ii].value, 10));
                                    }
                                    const newBrainAreaIds = brainAreaIds.filter((value) => !ids.includes(value));
                                    if (
                                        brainAreaIds.length !== newBrainAreaIds.length ||
                                        !brainAreaIds.every((value, index) => value === newBrainAreaIds[index])
                                    ) {
                                        updateBrainImage({ brainAreaIds: newBrainAreaIds });
                                    }
                                }
                            }}
                        />
                    </p>
                </div>
                <div className={styles.tabBrainAreaList}>
                    <select ref={brainAreaTarget} className={styles.tabBrainAreaSelect} size={15} multiple>
                        {brainAreaIds.map((value) => (
                            <option key={value} value={value}>
                                {NimgsearchUtil.getBrainAreaName(value)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    }, [brainAreaLevel, brainAreaIds, updateBrainImage]);

    const renderAdvancedSearchConditionTab = useCallback(() => {
        return (
            <div className={styles.tabItemRetrieval}>
                <table className={styles.tabItemRetrievalTable}>
                    <tbody>
                        <tr>
                            <th>Search area</th>
                            <th>Condition</th>
                        </tr>
                        <tr>
                            <td>Advanced Search</td>
                            <td>
                                <div className="advancedSearchCondition">
                                    <select
                                        value={itemType}
                                        onChange={(e) => {
                                            query.clear();
                                            setItemType(e.currentTarget.value);
                                        }}
                                    >
                                        <option value="">None</option>
                                        <option value="url">Url</option>
                                        <option value="paper">Paper</option>
                                        <option value="tool">Tool</option>
                                        <option value="model">Model</option>
                                        <option value="book">Book</option>
                                        <option value="data">Data</option>
                                        <option value="presentation">Presentation&amp;Doc</option>
                                    </select>
                                </div>
                                {itemType !== '' && (
                                    <>
                                        <div className="advancedSearchCondition">
                                            <span>
                                                <input
                                                    type="radio"
                                                    name="andOr"
                                                    onChange={() => setAndOr('and')}
                                                    checked={andOr === 'and'}
                                                />
                                                and{' '}
                                            </span>
                                            <span>
                                                <input
                                                    type="radio"
                                                    name="andOr"
                                                    onChange={() => setAndOr('or')}
                                                    checked={andOr === 'or'}
                                                />
                                                or{' '}
                                            </span>
                                        </div>
                                        <ItemType.AdvancedSearch
                                            key={itemType}
                                            type={`xnp${itemType}`}
                                            lang={lang}
                                            query={query}
                                        />
                                    </>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }, [itemType, andOr, query, lang]);

    const renderSearchResultTab = useCallback(() => {
        const startNum = 1 + sortCondition.limit * (sortCondition.page - 1);
        let endNum = sortCondition.limit * sortCondition.page;
        if (endNum > searchResult.total) {
            endNum = searchResult.total;
        }
        const maxPage =
            Math.floor(searchResult.total / sortCondition.limit) +
            (searchResult.total % sortCondition.limit !== 0 ? 1 : 0);
        const link = (title: string, page: number, key: string) => {
            if (page === 0 || page === sortCondition.page) {
                return (
                    <span key={key} className={styles.pageNaviItem}>
                        {title}
                    </span>
                );
            }
            return (
                <a
                    key={key}
                    className={styles.pageNaviItem}
                    href="/"
                    onClick={(e) => {
                        e.preventDefault();
                        const nextSortCondition = { ...sortCondition };
                        nextSortCondition.page = page;
                        updateSearchResults(nextSortCondition, coordType);
                    }}
                >
                    {title}
                </a>
            );
        };
        let startPage = Math.max(sortCondition.page - 4, 1);
        const endPage = Math.min(startPage + 9, maxPage);
        if (endPage - startPage < 9) {
            startPage = endPage - 9 > 0 ? endPage - 9 : 1;
        }
        const pageLinks: React.ReactElement[] = [];
        pageLinks.push(link('PREV', sortCondition.page - 1, 'p'));
        for (let i = startPage; i <= endPage; i++) {
            pageLinks.push(link(i.toString(), i, i.toString()));
        }
        pageLinks.push(link('NEXT', sortCondition.page >= maxPage ? 0 : sortCondition.page + 1, 'n'));
        return (
            <div className={styles.tabSearchResult}>
                {searchResult.total === 0 && (
                    <p>
                        {Functions.mlang(
                            '[en]No search results found.[/en][ja]該当するアイテムはありません[/ja]',
                            lang
                        )}
                    </p>
                )}
                {searchResult.total > 0 && (
                    <div>
                        <div>
                            <strong>{Functions.mlang('[en][Results][/en][ja][検索結果][/ja]', lang)}</strong>{' '}
                            {searchResult.total > 1 && (
                                <>
                                    <strong>{startNum}</strong> - <strong>{endNum}</strong>{' '}
                                    {Functions.mlang('[en]of[/en][ja]件目を表示[/ja]', lang)}{' '}
                                </>
                            )}
                            {Functions.mlang('[ja](全[/ja]', lang)}
                            <strong>{searchResult.total}</strong>
                            {Functions.mlang('[en] Items[/en][ja]件)[/ja]', lang)}
                        </div>
                        {maxPage > 1 && <div className={styles.tabSearchResultPageNavi}>{pageLinks}</div>}
                        <div className={styles.tabSearchResultSortCondition}>
                            <span>
                                {Functions.mlang('[en]Results per page[/en][ja]表示件数[/ja]', lang)}{' '}
                                <select
                                    value={sortCondition.limit}
                                    onChange={(e) => {
                                        const nextSortCondition = { ...sortCondition };
                                        nextSortCondition.limit = parseInt(e.target.value, 10) as SortConditionLimit;
                                        nextSortCondition.page = 1;
                                        updateSearchResults(nextSortCondition, coordType);
                                    }}
                                >
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </span>
                            <span>
                                Order by{' '}
                                <select
                                    value={sortCondition.orderBy}
                                    onChange={(e) => {
                                        const nextSortCondition = { ...sortCondition };
                                        nextSortCondition.orderBy = e.target.value as SortConditionOrderBy;
                                        updateSearchResults(nextSortCondition, coordType);
                                    }}
                                >
                                    <option value="title">
                                        {Functions.mlang('[en]Title[/en][ja]タイトル[/ja]', lang)}
                                    </option>
                                    <option value="doi">ID</option>
                                    <option value="last_update_date">
                                        {Functions.mlang('[en]Last Modified Date[/en][ja]最終更新日[/ja]', lang)}
                                    </option>
                                    <option value="creation_date">
                                        {Functions.mlang('[en]Created Date[/en][ja]作成日[/ja]', lang)}
                                    </option>
                                    <option value="publication_date">
                                        {Functions.mlang('[en]Date[/en][ja]日付[/ja]', lang)}
                                    </option>
                                </select>
                                <span>
                                    {sortCondition.orderDir === SortConditionOrderDir.DESC && (
                                        <>
                                            &#9660;{' '}
                                            <a
                                                href="/"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    const nextSortCondition = { ...sortCondition };
                                                    nextSortCondition.orderDir = SortConditionOrderDir.ASC;
                                                    updateSearchResults(nextSortCondition, coordType);
                                                }}
                                            >
                                                &#9650;
                                            </a>
                                        </>
                                    )}
                                    {sortCondition.orderDir === SortConditionOrderDir.ASC && (
                                        <>
                                            <a
                                                href="/"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    const nextSortCondition = { ...sortCondition };
                                                    nextSortCondition.orderDir = SortConditionOrderDir.DESC;
                                                    updateSearchResults(nextSortCondition, coordType);
                                                }}
                                            >
                                                &#9660;
                                            </a>{' '}
                                            &#9650;
                                        </>
                                    )}
                                </span>
                            </span>
                        </div>
                        <div className={styles.tabSearchResultItems}>
                            <ol start={startNum}>
                                {searchResult.data.map((item) => {
                                    return <li key={item.item_id}>{renderListItem(item)}</li>;
                                })}
                            </ol>
                        </div>
                        {maxPage > 1 && <div className={styles.tabSearchResultPageNavi}>{pageLinks}</div>}
                    </div>
                )}
            </div>
        );
    }, [sortCondition, searchResult, coordType, lang, updateSearchResults, renderListItem]);

    const renderMyItemsTab = useCallback(() => {
        return (
            <div className={styles.tabMyItems}>
                <h3>
                    {Functions.mlang(
                        '[en]The items which is display a spot[/en][ja]スポット表示中のアイテム[/ja]',
                        lang
                    )}
                </h3>
                {myItems.length > 0 ? (
                    <ol>
                        {myItems.map((item) => (
                            <li key={item.item_id}>{renderListItem(item)}</li>
                        ))}
                    </ol>
                ) : (
                    <p>{Functions.mlang('[en]No items found.[/en][ja]表示中のアイテムはありません[/ja]', lang)}</p>
                )}
            </div>
        );
    }, [myItems, lang, renderListItem]);

    return (
        <div className="nimgsearch">
            <Helmet>
                <title>NIMG Search - {Functions.siteTitle(lang)}</title>
            </Helmet>
            <div className={styles.container}>
                <div className={styles.headerPanel}>
                    <div className={styles.headerMessage}>
                        {Functions.mlang(
                            '[en]You can confirm the brain area and search the article.[/en][ja]脳部位の確認や論文の検索ができるページです[/ja]',
                            lang
                        )}
                    </div>
                    <div className={styles.headerLinks}>
                        {/* biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: the original toggles are plain text links */}
                        <span onClick={() => setShowSettings((prev) => !prev)}>
                            {Functions.mlang('[en][Setting][/en][ja]表示設定[/ja]', lang)}
                        </span>
                        {/* biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: the original toggles are plain text links */}
                        <span onClick={() => setShowHelp((prev) => !prev)}>
                            {Functions.mlang('[en][Help][/en][ja]ヘルプ[/ja]', lang)}
                        </span>
                    </div>
                </div>
                <div className={`${styles.mainPanel} ${imageSize}`}>
                    <div className={`${styles.brainPanel} ${imageSize}`}>
                        <div className={`${styles.brainViewY} ${imageSize}`}>
                            <canvas
                                ref={brainImageY}
                                onClick={(e) => onClickBrainImage(e, 'y')}
                                onMouseMove={(e) => onMouseMoveBrainImage(e, 'y')}
                            />
                        </div>
                        <div className={`${styles.brainViewX} ${imageSize}`}>
                            <canvas
                                ref={brainImageX}
                                onClick={(e) => onClickBrainImage(e, 'x')}
                                onMouseMove={(e) => onMouseMoveBrainImage(e, 'x')}
                            />
                        </div>
                        <div className={`${styles.brainViewZ} ${imageSize}`}>
                            <canvas
                                ref={brainImageZ}
                                onClick={(e) => onClickBrainImage(e, 'z')}
                                onMouseMove={(e) => onMouseMoveBrainImage(e, 'z')}
                            />
                        </div>
                        <div className={`${styles.coordView} ${imageSize}`}>
                            <span>{`(${pointer.x}, ${pointer.y}, ${pointer.z})`}</span>
                        </div>
                        <div className={`${styles.searchPanel} ${imageSize}`}>
                            <div className={styles.searchConditions}>
                                <span>
                                    x{' '}
                                    <input
                                        type="text"
                                        size={3}
                                        value={formCoord.x}
                                        onChange={(e) => onChnageCoordinate(e, 'x')}
                                    />
                                </span>
                                <span>
                                    y{' '}
                                    <input
                                        type="text"
                                        size={3}
                                        value={formCoord.y}
                                        onChange={(e) => onChnageCoordinate(e, 'y')}
                                    />
                                </span>
                                <span>
                                    z{' '}
                                    <input
                                        type="text"
                                        size={3}
                                        value={formCoord.z}
                                        onChange={(e) => onChnageCoordinate(e, 'z')}
                                    />
                                </span>
                                <span>
                                    <input
                                        type="button"
                                        value={Functions.mlang('[en]Update[/en][ja]更新[/ja]', lang)}
                                        onClick={onClickUpdateButton}
                                    />
                                </span>
                                <span>
                                    <input
                                        type="button"
                                        value={Functions.mlang('[en]Reset[/en][ja]戻す[/ja]', lang)}
                                        onClick={() => updateBrainImage({ coord: defaultCoord })}
                                    />
                                </span>
                            </div>
                            <div className={styles.searchConditions}>
                                <span>
                                    <input
                                        type="radio"
                                        name="radius"
                                        onChange={() => setFormRadiusRadio('2')}
                                        checked={formRadiusRadio === '2'}
                                    />
                                    2mm
                                </span>
                                <span>
                                    <input
                                        type="radio"
                                        name="radius"
                                        onChange={() => setFormRadiusRadio('6')}
                                        checked={formRadiusRadio === '6'}
                                    />
                                    6mm
                                </span>
                                <span>
                                    <input
                                        type="radio"
                                        name="radius"
                                        onChange={() => setFormRadiusRadio('')}
                                        checked={formRadiusRadio === ''}
                                    />
                                    <select
                                        value={formRadiusSelect}
                                        onChange={(e) => setFormRadiusSelect(e.target.value)}
                                    >
                                        {[2, 4, 6, 8, 10, 12].map((value) => (
                                            <option key={value} value={value}>
                                                {value}
                                            </option>
                                        ))}
                                    </select>
                                    mm
                                </span>
                            </div>
                            <div className={styles.searchConditions}>
                                <span>
                                    <input
                                        type="radio"
                                        name="coordType"
                                        onChange={() => updateBrainImage({ formCoordType: 'mni' })}
                                        checked={formCoordType === 'mni'}
                                    />
                                    MNI
                                </span>
                                <span>
                                    <input
                                        type="radio"
                                        name="coordType"
                                        onChange={() => updateBrainImage({ formCoordType: 'talairach' })}
                                        checked={formCoordType === 'talairach'}
                                    />
                                    Talairach
                                </span>
                                <span>
                                    <input
                                        type="radio"
                                        name="coordType"
                                        onChange={() => updateBrainImage({ formCoordType: 'unspecified' })}
                                        checked={formCoordType === 'unspecified'}
                                    />
                                    Not Specified
                                </span>
                            </div>
                            <div className={styles.searchConditions}>
                                <span>
                                    <input
                                        type="radio"
                                        name="paperType"
                                        onChange={() => setFormPaperType('all')}
                                        checked={formPaperType === 'all'}
                                    />
                                    All
                                </span>
                                <span>
                                    <input
                                        type="radio"
                                        name="paperType"
                                        onChange={() => setFormPaperType('normal')}
                                        checked={formPaperType === 'normal'}
                                    />
                                    Normal
                                </span>
                                <span>
                                    <input
                                        type="radio"
                                        name="paperType"
                                        onChange={() => setFormPaperType('patients')}
                                        checked={formPaperType === 'patients'}
                                    />
                                    Patients
                                </span>
                            </div>
                            <div className={styles.searchButton}>
                                <span>
                                    <input
                                        type="button"
                                        value={Functions.mlang(
                                            '[en]Search Related-papers[/en][ja]　　　検索　　　[/ja]',
                                            lang
                                        )}
                                        onClick={() => updateSearchResults(defaultSortCondition, formCoordType)}
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                    {(showSettings || showHelp) && (
                        <div className={styles.popupPanel}>
                            {showSettings && (
                                <div className={styles.settings}>
                                    <dl>
                                        <dt>{Functions.mlang('[en]Image-Size[/en][ja]画像サイズ[/ja]', lang)}</dt>
                                        <dd>
                                            <span>
                                                <input
                                                    type="radio"
                                                    name="imageSize"
                                                    onChange={() => updateBrainImage({ imageSize: 'large' })}
                                                    checked={imageSize === 'large'}
                                                />
                                                {Functions.mlang('[en]Large[/en][ja]大[/ja]', lang)}
                                            </span>
                                            <span>
                                                <input
                                                    type="radio"
                                                    name="imageSize"
                                                    onChange={() => updateBrainImage({ imageSize: 'medium' })}
                                                    checked={imageSize === 'medium'}
                                                />
                                                {Functions.mlang('[en]Medium[/en][ja]中[/ja]', lang)}
                                            </span>
                                            <span>
                                                <input
                                                    type="radio"
                                                    name="imageSize"
                                                    onChange={() => updateBrainImage({ imageSize: 'small' })}
                                                    checked={imageSize === 'small'}
                                                />
                                                {Functions.mlang('[en]Small[/en][ja]小[/ja]', lang)}
                                            </span>
                                        </dd>
                                        <dt>{Functions.mlang('[en]On/Off[/en][ja]表示切替[/ja]', lang)}</dt>
                                        <dd>
                                            <span>
                                                <input
                                                    type="checkbox"
                                                    onChange={() => updateBrainImage({ showLines: !showLines })}
                                                    checked={showLines}
                                                />
                                                {Functions.mlang('[en]Show Lines[/en][ja]ラインを表示[/ja]', lang)}
                                            </span>
                                            <span>
                                                <input
                                                    type="checkbox"
                                                    onChange={() => updateBrainImage({ showSpots: !showSpots })}
                                                    checked={showSpots}
                                                />
                                                {Functions.mlang('[en]Show Spots[/en][ja]スポットを表示[/ja]', lang)}
                                            </span>
                                        </dd>
                                    </dl>
                                </div>
                            )}
                            {showHelp && (
                                <div className={styles.help}>
                                    <div>
                                        <span>
                                            {Functions.mlang('[en]Demo of usage[/en][ja]使い方のデモ[/ja]', lang)}
                                        </span>
                                    </div>
                                    <div>
                                        {/* biome-ignore lint/a11y/useMediaCaption: the demo has no speech */}
                                        <video width={240} height={210} poster="./help/demo.png" controls>
                                            <source src="./help/demo.mp4" type="video/mp4" />
                                            <source src="./help/demo.webm" type="video/webm" />
                                        </video>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.footerPanel}>
                    <ul className={styles.navTabs}>
                        {/* biome-ignore lint/a11y/useKeyWithClickEvents: tabs of the original page */}
                        <li className={tab === 0 ? 'actived' : 'normal'} onClick={() => setTab(0)}>
                            {Functions.mlang('[en]Brain area[/en][ja]脳部位[/ja]', lang)}
                        </li>
                        {/* biome-ignore lint/a11y/useKeyWithClickEvents: tabs of the original page */}
                        <li className={tab === 1 ? 'actived' : 'normal'} onClick={() => setTab(1)}>
                            {Functions.mlang('[en]Item retrieval[/en][ja]アイテム検索[/ja]', lang)}
                        </li>
                        {/* biome-ignore lint/a11y/useKeyWithClickEvents: tabs of the original page */}
                        <li className={tab === 2 ? 'actived' : 'normal'} onClick={() => setTab(2)}>
                            {Functions.mlang('[en]Result of retrieval[/en][ja]検索結果[/ja]', lang)}
                        </li>
                        {/* biome-ignore lint/a11y/useKeyWithClickEvents: tabs of the original page */}
                        <li className={tab === 3 ? 'actived' : 'normal'} onClick={() => setTab(3)}>
                            {Functions.mlang('[en]My items[/en][ja]マイアイテム[/ja]', lang)}
                        </li>
                    </ul>
                    <div className={styles.tabContent}>
                        {tab === 0 && renderBrainAreaTab()}
                        {tab === 1 && renderAdvancedSearchConditionTab()}
                        {tab === 2 && renderSearchResultTab()}
                        {tab === 3 && renderMyItemsTab()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Nimgsearch;
